import { Playground, PLAYGROUNG_BG_LIGHTEST } from '../../../components/playground/Playground';
import {
    PlaygroundClasses,
    PlaygroundClassesSpacer,
    PlaygroundResponsiveClasses,
} from '../../../components/playground/PlaygroundClasses';
import { Description } from '../../../components/Description';
import { Headline } from '../../../components/Headline';
import { dummyTextLong } from '../../../utils/data';

const columnCounts = ['1', '2', '3', '4', '5', '6'];
const columnWidths = ['10ch', '15ch', '20ch', '25ch', '30ch', '35ch', '40ch'];
const columnCountWidthExamples = ['2-20ch', '3-15ch', '4-20ch', '5-15ch', '6-10ch'];

const paragraphTexts = dummyTextLong.trim().split('\n\n');

const cardItems = [
    {
        title: 'Planning',
        text: 'Use columns when cards should follow a simple reading flow without manual grid placement.',
    },
    {
        title: 'Operations',
        text: 'Combine columns with column-gap classes to keep repeated content compact and readable.',
    },
    {
        title: 'Reporting',
        text: 'Switch the column count responsively while the card markup stays unchanged.',
    },
];

export const Columns = () => (
    <div>
        <Headline anchor='columns'>Columns</Headline>
        <Description>
            <p className='margin-top-0'>
                Columns classes create typographic multi-column layouts for flowing text-like content into balanced
                columns, similar to newspaper or documentation layouts. Combine them with{' '}
                <code className='code-inline'>gap-*</code> or <code className='code-inline'>column-gap-*</code> to
                control the space between columns.
            </p>
            <p>
                The responsive suffixes can switch column behavior by breakpoint, for example{' '}
                <code className='code-inline'>columns-1 columns-2-ls columns-3-md</code>. Use grid classes when children
                need independent placement; use columns classes when text-like content should flow automatically.
            </p>
            <div className='text-medium margin-bottom-5'>Basic usage</div>
            <ul className='margin-bottom-15'>
                <li>
                    Use <code className='code-inline'>columns-2</code> when only the column count should be controlled.
                </li>
                <li>
                    Use <code className='code-inline'>columns-20ch</code> when only the preferred column width should be
                    controlled.
                </li>
                <li>
                    Use <code className='code-inline'>columns-2-20ch</code> when count and width should be set together.
                </li>
                <li>
                    Avoid combining count and width classes separately because they both set the same CSS{' '}
                    <code className='code-inline'>columns</code> shorthand.
                </li>
            </ul>
            <p>
                Column width classes use <code className='code-inline'>ch</code> units because multi-column layouts are
                typographic by nature. One <code className='code-inline'>ch</code> is based on the width of the{' '}
                <code className='code-inline'>0</code> character in the current font, so values such as{' '}
                <code className='code-inline'>columns-30ch</code> scale with text size instead of mapping to a fixed
                pixel width. Use <code className='code-inline'>break-inside-avoid</code> on cards, panels, tiles, and
                other non-text elements inside columns so a single item does not split across two columns.
            </p>
        </Description>
        <Playground example={columnsExample} classes={columnsClasses} background={PLAYGROUNG_BG_LIGHTEST} />
    </div>
);

const columnsExample = (
    <div className='display-grid gap-25'>
        <div>
            <div className='text-medium margin-bottom-10'>
                Balanced text columns with gap using responsive breakpoints
            </div>
            <div className='columns-1 columns-2-ls columns-3-md gap-25 line-height-150rel'>
                {paragraphTexts.map((paragraphText, index) => (
                    <p className='margin-top-0' key={index}>
                        {paragraphText}
                    </p>
                ))}
            </div>
        </div>
        <div>
            <div className='text-medium margin-bottom-10'>Count and width shorthand with column rule</div>
            <div className='columns-3-20ch column-gap-25 column-rule line-height-150rel'>
                {paragraphTexts.map((paragraphText, index) => (
                    <p className='margin-top-0' key={index}>
                        {paragraphText}
                    </p>
                ))}
            </div>
        </div>
        <div>
            <div className='text-medium margin-bottom-10'>Cards in columns</div>
            <div className='columns-2 columns-3-md gap-15'>
                {cardItems.map(cardItem => (
                    <div
                        className='panel panel-default panel-body shadow-default margin-bottom-15 break-inside-avoid'
                        key={cardItem.title}
                    >
                        <div className='text-medium margin-bottom-10'>{cardItem.title}</div>
                        <p className='margin-0'>{cardItem.text}</p>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const columnsClasses = (
    <>
        <PlaygroundClasses label='Reset' classes='columns-auto' />
        <PlaygroundClassesSpacer label='Column count' />
        <PlaygroundClasses label='Count' prefix='columns-' classes={columnCounts} />
        <PlaygroundClassesSpacer label='Column width' />
        <PlaygroundClasses label='Width' prefix='columns-' classes={columnWidths} />
        <PlaygroundClassesSpacer label='Columns shorthand' />
        <PlaygroundClasses label='Count + width' prefix='columns-' classes={columnCountWidthExamples} />
        <PlaygroundClassesSpacer label='Column rule' />
        <PlaygroundClasses label='Rule' classes={['column-rule', 'column-rule-none']} />
        <PlaygroundClassesSpacer label='Column breaks' />
        <PlaygroundClasses
            label='Avoid breaks'
            classes={
                <>
                    <code>break-inside-avoid</code>
                    <span>Use for cards, panels, and other non-text elements that should stay in one column.</span>
                </>
            }
        />
        <PlaygroundClassesSpacer label='Combinations' guidelinesLink='/foundations/gap' />
        <PlaygroundClasses
            label='Gap'
            classes={
                <>
                    <code>columns-3 gap-20</code>
                    <code>columns-3-15ch column-gap-25</code>
                    <code>columns-3-md column-gap-25</code>
                    <code>columns-3 gap-25 break-inside-avoid</code>
                </>
            }
        />
        <PlaygroundResponsiveClasses />
    </>
);
